import React, { useState, useEffect } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import ReactMarkdown from 'react-markdown';
import Header from '../components/Header';
import Footer from '../components/Footer';
import { getArticleBySlug, getArticlesByCategory, getArticleReactions, postArticleReaction, recordArticleView } from '../services/api';
import { useDocumentTitle } from '../utils/useDocumentTitle';

// Import the icon components
const TechIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" fill="currentColor" viewBox="0 0 16 16">
    <path d="M5 0a.5.5 0 0 1 .5.5V2h1V.5a.5.5 0 0 1 1 0V2h1V.5a.5.5 0 0 1 1 0V2A2.5 2.5 0 0 1 11 4.5h2V14a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V4.5h2A2.5 2.5 0 0 1 7 2V.5A.5.5 0 0 1 7.5 0zM3 4.5A3.5 3.5 0 0 0 6.5 8h3A3.5 3.5 0 0 0 13 4.5z"/>
  </svg>
);

const LearningIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" fill="currentColor" viewBox="0 0 16 16">
    <path d="M1 2.828c.885-.37 2.154-.769 3.388-.893 1.33-.134 2.458.063 3.112.752v9.746c-.935-.53-2.12-.603-3.213-.493-1.18.12-2.37.461-3.287.811V2.828zm7.5-.141c.654-.689 1.782-.886 3.112-.752 1.234.124 2.503.523 3.388.893v9.923c-.918-.35-2.107-.692-3.287-.81-1.094-.111-2.278-.039-3.213.492V2.687zM8 1.783C7.015.936 5.587.81 4.287.94c-1.514.153-3.042.672-3.994 1.105A.5.5 0 0 0 0 2.5v11a.5.5 0 0 0 .707.455c.882-.4 2.303-.881 3.68-1.02 1.409-.142 2.59.087 3.223.877a.5.5 0 0 0 .78 0c.633-.79 1.814-1.019 3.222-.877 1.378.139 2.8.62 3.681 1.02A.5.5 0 0 0 16 13.5v-11a.5.5 0 0 0-.293-.455c-.952-.433-2.48-.952-3.994-1.105C10.413.809 8.985.936 8 1.783z"/>
  </svg>
);

const ArchitectureIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" fill="currentColor" viewBox="0 0 16 16">
    <path d="M14.763.075A.5.5 0 0 1 15 .5v15a.5.5 0 0 1-.5.5h-3a.5.5 0 0 1-.5-.5V14h-1v1.5a.5.5 0 0 1-.5.5h-9a.5.5 0 0 1-.5-.5V10a.5.5 0 0 1 .342-.474L6 7.64V4.5a.5.5 0 0 1 .276-.447l8-4a.5.5 0 0 1 .487.022ZM6 8.694 1 10.36V15h5V8.694ZM7 15h2v-1.5a.5.5 0 0 1 .5-.5h2a.5.5 0 0 1 .5.5V15h2V1.309l-7 3.5V15Z"/>
    <path d="M2 11h1v1H2v-1Zm2 0h1v1H4v-1Zm-2 2h1v1H2v-1Zm2 0h1v1H4v-1Zm4-4h1v1H8V9Zm2 0h1v1h-1V9Zm-2 2h1v1H8v-1Zm2 0h1v1h-1v-1Zm2-2h1v1h-1V9Zm0 2h1v1h-1v-1ZM8 7h1v1H8V7Zm2 0h1v1h-1V7Zm2 0h1v1h-1V7ZM8 5h1v1H8V5Zm2 0h1v1h-1V5Zm2 0h1v1h-1V5Zm0-2h1v1h-1V3Z"/>
  </svg>
);

const TeamIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" fill="currentColor" viewBox="0 0 16 16">
    <path d="M7 14s-1 0-1-1 1-4 5-4 5 3 5 4-1 1-1 1H7Zm4-6a3 3 0 1 0 0-6 3 3 0 0 0 0 6Zm-5.784 6A2.238 2.238 0 0 1 5 13c0-1.355.68-2.75 1.936-3.72A6.325 6.325 0 0 0 5 9c-4 0-5 3-5 4s1 1 1 1h4.216ZM4.5 8a2.5 2.5 0 1 0 0-5 2.5 2.5 0 0 0 0 5Z"/>
  </svg>
);

// Sample data for all articles
const allCategories = [
  {
    id: 1,
    title: "Tech Trends",
    icon: <TechIcon />,
    articles: [
      {
        id: 101,
        title: "The Rise of AI in Software Development",
        description: "Explore how artificial intelligence is transforming the way we build and maintain software.",
        author: "Sarah Chen",
        date: "June 15, 2023",
        slug: "rise-of-ai-in-software-development",
        image: "https://images.pexels.com/photos/8386440/pexels-photo-8386440.jpeg?auto=compress&cs=tinysrgb&w=1260&h=750&dpr=1",
        content: `
          <p>The landscape of software development is rapidly evolving with artificial intelligence playing an increasingly significant role in streamlining processes. One area seeing remarkable transformation is code reviews, where AI tools are now augmenting human reviewers to improve efficiency and code quality.</p>
          
          <h2>Key Benefits of AI-Assisted Development</h2>
          <ul>
            <li>Automated detection of common bugs and security vulnerabilities</li>
            <li>Consistent enforcement of coding standards and best practices</li>
            <li>Reduction in development time by up to 60%</li>
            <li>Identification of potential performance issues before deployment</li>
            <li>Continuous learning from codebase patterns and previous reviews</li>
          </ul>
          
          <h2>Implementation Strategies</h2>
          <p>Our DevOps team has recently integrated AI tools into our development pipeline. The initial results have been promising, with a 40% reduction in critical bugs making it to production and a 30% decrease in the time developers spend on code reviews.</p>
          
          <blockquote>"The AI doesn't replace human developers, but it handles the routine checks so we can focus on architectural considerations and complex logic," explains Michael Wong, Lead DevOps Engineer.</blockquote>
          
          <h2>Getting Started with AI-Assisted Development</h2>
          <p>For teams interested in implementing AI tools in their development workflow, we recommend starting with these steps:</p>
          <ol>
            <li>Evaluate available tools based on your tech stack and requirements</li>
            <li>Start with a pilot project to measure impact and gather feedback</li>
            <li>Customize rules and sensitivity to match your team's coding standards</li>
            <li>Provide training to help developers interpret and act on AI suggestions</li>
            <li>Establish clear guidelines on when human intervention is still required</li>
          </ol>
          
          <h2>Future Directions</h2>
          <p>The next generation of AI development tools is expected to offer even more sophisticated capabilities, including automated code refactoring suggestions, intelligent test generation, and predictive analysis of potential technical debt.</p>
        `
      },
      {
        id: 102,
        title: "Web3 and the Future of Internet",
        description: "Understand the core concepts of Web3 and how it promises to reshape our digital interactions.",
        author: "Alex Rivera",
        date: "June 10, 2023",
        slug: "web3-future-of-internet",
        image: "https://images.pexels.com/photos/8451490/pexels-photo-8451490.jpeg?auto=compress&cs=tinysrgb&w=1260&h=750&dpr=1",
        content: `
          <p>Web3 represents the next evolution of the internet, promising to transform how we interact with digital services and each other online. Unlike the current centralized model, Web3 emphasizes decentralization, user ownership, and greater privacy controls.</p>
          
          <h2>Key Features of Web3</h2>
          <ul>
            <li>Decentralized infrastructure based on blockchain technology</li>
            <li>User ownership of data and digital assets</li>
            <li>Trustless interactions without centralized intermediaries</li>
            <li>Token-based economics and incentives</li>
            <li>Enhanced privacy and security mechanisms</li>
          </ul>
          
          <h2>Potential Applications</h2>
          <p>Web3 technology is already enabling new applications across various domains:</p>
          
          <h3>Decentralized Finance (DeFi)</h3>
          <p>Financial services without traditional banks, including lending, borrowing, and trading.</p>
          
          <h3>Digital Identity</h3>
          <p>Self-sovereign identity systems that give users control over their personal information.</p>
          
          <h3>Content Distribution</h3>
          <p>Direct creator-to-consumer models without centralized platforms taking large cuts.</p>
          
          <h2>Challenges Ahead</h2>
          <p>Despite its promise, Web3 faces significant hurdles to mainstream adoption:</p>
          <ol>
            <li>Scalability limitations of current blockchain technologies</li>
            <li>Complex user experiences compared to Web2 applications</li>
            <li>Regulatory uncertainty across different jurisdictions</li>
            <li>Environmental concerns related to proof-of-work consensus mechanisms</li>
          </ol>
          
          <h2>The Path Forward</h2>
          <p>For Web3 to achieve its potential, the industry needs to focus on improving scalability, simplifying user interfaces, and working with regulators to establish clear frameworks that protect users while enabling innovation.</p>
        `
      },
      // Other tech trends articles would be defined here
    ]
  },
  {
    id: 2,
    title: "Learning Corner",
    icon: <LearningIcon />,
    articles: [
      {
        id: 201,
        title: "Getting Started with React 18",
        description: "A comprehensive guide to the latest features in React 18 and how to leverage them.",
        author: "Maya Johnson",
        date: "June 8, 2023",
        slug: "getting-started-react-18",
        image: "https://images.pexels.com/photos/11035380/pexels-photo-11035380.jpeg?auto=compress&cs=tinysrgb&w=1260&h=750&dpr=1",
        content: `
          <p>React 18 introduces several powerful features that improve performance and user experience in your applications. This guide will help you understand and implement these new capabilities.</p>
          
          <h2>Key Features in React 18</h2>
          <ul>
            <li>Automatic Batching for better performance</li>
            <li>Concurrent Rendering to keep UI responsive</li>
            <li>Transitions for smoother user experiences</li>
            <li>Suspense improvements for better loading states</li>
            <li>New APIs including useId, useTransition, and useDeferredValue</li>
          </ul>
          
          <h2>Setting Up React 18</h2>
          <p>To get started with React 18, you'll need to update your dependencies and modify your index.js file:</p>
          
          <pre><code>
          // Install the latest versions
          npm install react@latest react-dom@latest
          
          // Update your index.js
          import { createRoot } from 'react-dom/client';
          
          const container = document.getElementById('root');
          const root = createRoot(container);
          root.render(&lt;App />);
          </code></pre>
          
          <h2>Using Concurrent Features</h2>
          <p>Concurrent Mode is now enabled by default, but you need to opt into specific features like transitions:</p>
          
          <pre><code>
          import { useTransition } from 'react';
          
          function MyComponent() {
            const [isPending, startTransition] = useTransition();
            
            function handleClick() {
              startTransition(() => {
                // Expensive state update
                setItems(generateLargeList());
              });
            }
            
            // Rest of component
          }
          </code></pre>
          
          <h2>Best Practices</h2>
          <p>When working with React 18:</p>
          <ol>
            <li>Use startTransition for non-urgent updates</li>
            <li>Implement Suspense boundaries for loading states</li>
            <li>Leverage automatic batching for performance</li>
            <li>Consider using the new hooks for specialized use cases</li>
          </ol>
          
          <h2>Migration Considerations</h2>
          <p>Most applications should work without changes, but you may need to review code that:</p>
          <ul>
            <li>Relies on specific rendering behavior or timing</li>
            <li>Uses custom renderers or interacts with internals</li>
            <li>Depends on deprecated features or unstable APIs</li>
          </ul>
        `
      },
      {
        id: 202,
        title: "Understanding TypeScript Generics",
        description: "Master the powerful concept of generics in TypeScript with practical examples.",
        author: "David Kim",
        date: "May 28, 2023",
        slug: "understanding-typescript-generics",
        image: "https://images.pexels.com/photos/11035471/pexels-photo-11035471.jpeg?auto=compress&cs=tinysrgb&w=1260&h=750&dpr=1",
        content: `
          <p>TypeScript generics allow you to create reusable components that work with a variety of types rather than a single one. This flexibility helps you build more robust and type-safe code.</p>
          
          <h2>Basic Generic Syntax</h2>
          <pre><code>
          function identity&lt;T>(arg: T): T {
            return arg;
          }
          
          // Usage
          let output1 = identity&lt;string>("myString");  // type is string
          let output2 = identity(123);  // type inference determines number
          </code></pre>
          
          <h2>Generic Interfaces</h2>
          <p>Generics can be used with interfaces to create flexible type contracts:</p>
          
          <pre><code>
          interface Box&lt;T> {
            contents: T;
          }
          
          let stringBox: Box&lt;string> = { contents: "hello" };
          let numberBox: Box&lt;number> = { contents: 42 };
          </code></pre>
          
          <h2>Generic Constraints</h2>
          <p>You can limit the types that can be used with your generics using constraints:</p>
          
          <pre><code>
          interface Lengthwise {
            length: number;
          }
          
          function logLength&lt;T extends Lengthwise>(arg: T): T {
            console.log(arg.length);  // Now we know it has a .length property
            return arg;
          }
          
          // Works with strings (which have length)
          logLength("hello");
          
          // Works with arrays (which have length)
          logLength([1, 2, 3]);
          
          // Error: number doesn't have a .length property
          // logLength(123);
          </code></pre>
          
          <h2>Generic Classes</h2>
          <p>Classes can also leverage generics for maximum flexibility:</p>
          
          <pre><code>
          class GenericNumber&lt;T> {
            zeroValue: T;
            add: (x: T, y: T) => T;
          }
          
          let stringNumeric = new GenericNumber&lt;string>();
          stringNumeric.zeroValue = "";
          stringNumeric.add = function(x, y) { return x + y; };
          
          console.log(stringNumeric.add("Hello ", "World"));  // "Hello World"
          </code></pre>
          
          <h2>Common Use Cases</h2>
          <ul>
            <li>Reusable data structures (lists, maps, etc.)</li>
            <li>API responses with varying payload types</li>
            <li>Factory functions that produce objects of different types</li>
            <li>State management where state structure varies</li>
          </ul>
          
          <h2>Advanced Patterns</h2>
          <p>As you get more comfortable with generics, explore advanced patterns like:</p>
          <ul>
            <li>Multiple type parameters</li>
            <li>Generic type inference</li>
            <li>Using keyof with generics</li>
            <li>Conditional types based on generics</li>
          </ul>
        `
      },
      // Other learning corner articles would be defined here
    ]
  },
  {
    id: 3,
    title: "Software Architecture",
    icon: <ArchitectureIcon />,
    articles: [
      {
        id: 301,
        title: "Microservices vs Monoliths",
        description: "Analyzing the pros and cons of different architectural approaches for modern applications.",
        author: "James Wilson",
        date: "May 22, 2023",
        slug: "microservices-vs-monoliths",
        image: "https://images.pexels.com/photos/1181467/pexels-photo-1181467.jpeg?auto=compress&cs=tinysrgb&w=1260&h=750&dpr=1",
        content: `
          <p>Choosing between a microservices or monolithic architecture is a critical decision that impacts development speed, scalability, and maintenance. This article compares both approaches to help you make an informed choice for your next project.</p>
          
          <h2>Monolithic Architecture</h2>
          <p>A monolithic application is built as a single, autonomous unit where all components are interconnected and interdependent.</p>
          
          <h3>Advantages of Monoliths:</h3>
          <ul>
            <li>Simpler development experience for small teams</li>
            <li>Easier testing and debugging</li>
            <li>Faster initial development and deployment</li>
            <li>Less operational complexity</li>
            <li>Lower infrastructure costs for smaller applications</li>
          </ul>
          
          <h3>Challenges with Monoliths:</h3>
          <ul>
            <li>Scaling becomes difficult as the application grows</li>
            <li>Technology stack is fixed for the entire application</li>
            <li>Reliability issues (one bug can potentially bring down the entire system)</li>
            <li>Codebase becomes harder to understand as it grows</li>
            <li>Continuous deployment becomes challenging</li>
          </ul>
          
          <h2>Microservices Architecture</h2>
          <p>Microservices architecture structures an application as a collection of loosely coupled services, each implementing a specific business function.</p>
          
          <h3>Advantages of Microservices:</h3>
          <ul>
            <li>Independent deployment of services</li>
            <li>Technology diversity (different services can use different tech stacks)</li>
            <li>Improved fault isolation and resilience</li>
            <li>Easier to scale specific components based on demand</li>
            <li>Better alignment with team structures (Conway's Law)</li>
          </ul>
          
          <h3>Challenges with Microservices:</h3>
          <ul>
            <li>Increased operational complexity</li>
            <li>Distributed system challenges (network latency, potential data inconsistency)</li>
            <li>More complex testing, especially integration testing</li>
            <li>Debugging across service boundaries is difficult</li>
            <li>Higher infrastructure costs</li>
          </ul>
          
          <h2>Decision Framework</h2>
          <p>Consider these factors when choosing between architectures:</p>
          
          <h3>Choose Monolith When:</h3>
          <ul>
            <li>Building a startup or MVP where speed is critical</li>
            <li>Working with a small team with limited operational resources</li>
            <li>Application domain is not yet well understood</li>
            <li>Application is expected to remain relatively simple</li>
          </ul>
          
          <h3>Choose Microservices When:</h3>
          <ul>
            <li>Building a complex application with distinct bounded contexts</li>
            <li>Scaling a large development organization across multiple teams</li>
            <li>Different components have different scaling needs</li>
            <li>Need to leverage different technology stacks for different components</li>
          </ul>
          
          <h2>Hybrid Approaches</h2>
          <p>Many successful systems use a hybrid approach, starting with a modular monolith and extracting microservices only where necessary. This "monolith-first" strategy, advocated by Martin Fowler, allows teams to understand domain boundaries before committing to service decomposition.</p>
        `
      },
      // Other software architecture articles would be defined here
    ]
  },
  {
    id: 4,
    title: "Team Achievements",
    icon: <TeamIcon />,
    articles: [
      {
        id: 401,
        title: "AI Team Implements New ML Pipeline",
        description: "Our AI team has successfully implemented a new machine learning pipeline that reduces training time by 40%.",
        author: "Team Lead",
        date: "June 20, 2023",
        slug: "ai-team-ml-pipeline",
        image: "https://images.pexels.com/photos/8386434/pexels-photo-8386434.jpeg?auto=compress&cs=tinysrgb&w=1260&h=750&dpr=1",
        content: `
          <p>The AI research team has successfully implemented a new machine learning pipeline that represents a significant advancement in our data processing capabilities. The new system reduces model training time by 40% while improving overall accuracy.</p>
          
          <h2>Key Improvements</h2>
          <ul>
            <li>Modernized data preprocessing workflow with parallel processing</li>
            <li>Implemented advanced feature engineering techniques</li>
            <li>Deployed distributed training across multiple GPU clusters</li>
            <li>Created automated hyperparameter tuning system</li>
            <li>Built comprehensive model evaluation dashboard</li>
          </ul>
          
          <h2>Technical Implementation</h2>
          <p>The team leveraged several cutting-edge technologies in this implementation:</p>
          
          <h3>Data Processing</h3>
          <p>The pipeline uses Apache Beam for scalable data processing, allowing us to handle significantly larger datasets than before. Custom transforms were implemented for domain-specific feature engineering.</p>
          
          <h3>Training Infrastructure</h3>
          <p>Training now runs on a Kubernetes cluster with specialized GPU nodes, managed through custom scheduling logic that optimizes resource utilization across teams.</p>
          
          <h3>Model Monitoring</h3>
          <p>A comprehensive monitoring system tracks model performance, drift detection, and provides alerting capabilities when models require retraining.</p>
          
          <h2>Business Impact</h2>
          <p>The improved pipeline has already shown significant business value:</p>
          <ul>
            <li>40% reduction in training time enables faster iteration cycles</li>
            <li>7% improvement in model prediction accuracy</li>
            <li>50% reduction in false positives for critical use cases</li>
            <li>Ability to incorporate 3x more training data within the same processing window</li>
          </ul>
          
          <h2>Team Recognition</h2>
          <p>Special recognition goes to:</p>
          <ul>
            <li>Dr. Sarah Chen for architecture design</li>
            <li>Michael Wong for infrastructure implementation</li>
            <li>The entire data engineering team for optimizing data flows</li>
            <li>DevOps team for their assistance with deployment automation</li>
          </ul>
          
          <blockquote>"This project exemplifies what we can achieve when we combine deep expertise in machine learning with solid engineering practices," notes the AI Director. "The pipeline will serve as a foundation for all our future ML initiatives."</blockquote>
        `
      },
      // Other team achievement articles would be defined here
    ]
  }
];

function ArticleDetail() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const [article, setArticle] = useState(null);
  const [relatedArticles, setRelatedArticles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [reactions, setReactions] = useState({ like: 0, love: 0, insightful: 0, curious: 0, celebrate: 0 });
  const [reacting, setReacting] = useState(false);

  useEffect(() => {
    const fetchArticleData = async () => {
      setLoading(true);
      setError(null);
      try {
        const articleResponse = await getArticleBySlug(slug);
        if (articleResponse && articleResponse.data) {
          setArticle(articleResponse.data);
          try {
            await recordArticleView(articleResponse.data._id);
          } catch (_) {}
          try {
            const r = await getArticleReactions(articleResponse.data._id);
            if (r && r.counts) setReactions(r.counts);
          } catch (_) {}
          // Fetch related articles only if the main article was found
          if (articleResponse.data.category && articleResponse.data.category._id) {
            const relatedResponse = await getArticlesByCategory(
              { _id: articleResponse.data.category._id },
              1, // page 1
              4  // limit to 3 related + current article makes 4
            );
            if (relatedResponse && relatedResponse.data) {
              // Filter out the current article from related articles
              setRelatedArticles(
                relatedResponse.data.filter(relArt => relArt.slug !== slug).slice(0, 3)
              );
            }
          }
        } else {
          setError('Article not found or failed to load.');
        }
      } catch (err) {
        console.error('Error fetching article data:', err);
        setError(err.message || 'An error occurred while fetching the article.');
      }
      setLoading(false);
    };

    if (slug) {
      fetchArticleData();
    }
  }, [slug]);

  const getClientId = () => {
    const key = 'rs_react_client_id';
    let cid = localStorage.getItem(key);
    if (!cid) {
      cid = Math.random().toString(36).slice(2) + Date.now().toString(36);
      localStorage.setItem(key, cid);
    }
    return cid;
  };

  const handleReact = async (type) => {
    if (!article || reacting) return;
    setReacting(true);
    try {
      const res = await postArticleReaction(article._id, type, getClientId());
      if (res && res.counts) setReactions(res.counts);
    } catch (_) {}
    setReacting(false);
  };

  const getCategoryIcon = (category) => {
    if (!category || !category.name) return null;
    const categoryName = category.name.toLowerCase();
    if (categoryName.includes('tech')) return <TechIcon />;
    if (categoryName.includes('learn')) return <LearningIcon />;
    if (categoryName.includes('architect')) return <ArchitectureIcon />;
    if (categoryName.includes('team')) return <TeamIcon />;
    return null; 
  };

  // Set document title based on component state
  useDocumentTitle(
    loading ? 'Loading Article - RealSoft Newsletter' :
    error || !article ? 'Article Not Found - RealSoft Newsletter' :
    `${article.title} - RealSoft Newsletter`
  );

  if (loading) {
    return (
      <>
        <Header />
        <div className="container section text-center"><p>Loading article...</p></div>
        <Footer />
      </>
    );
  }

  if (error) {
    return (
      <>
        <Header />
        <div className="container section error-message"><p>{error}</p></div>
        <Footer />
      </>
    );
  }

  if (!article) {
    return (
      <>
        <Header />
        <div className="container section text-center"><p>Article not found.</p></div>
        <Footer />
      </>
    );
  }

  const { title, content, author, date, category, image, description, contentType } = article;
  const formattedDate = date ? new Date(date).toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' }) : 'Date not available';

  return (
    <>
      <Header />
      <div className="article-detail-page container section">
        <nav className="breadcrumbs text-sm text-gray-600 mb-6">
          <Link to='/'>Home</Link> / 
          {category && <Link to={`/archive?category=${category.slug || category.name.toLowerCase()}`}>{category.name}</Link>} / 
          <span className="font-medium text-gray-800">{title}</span>
        </nav>

        <article className="bg-white shadow-xl rounded-lg overflow-hidden">
          {image && (
            <img src={image} alt={title} className="w-full h-auto md:h-96 object-cover" />
          )}
          <div className="p-6 md:p-10">
            <div className="mb-4 flex items-center text-sm text-gray-500">
              {category && category.name && (
                <span className="inline-flex items-center mr-4">
                  {getCategoryIcon(category)}
                  <span className="ml-2">{category.name}</span>
                </span>
              )}
              <span>{formattedDate}</span>
            </div>
            <h1 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4 leading-tight">{title}</h1>
            {author && <p className="text-gray-700 mb-6 text-lg"><span className="font-semibold">By:</span> {author}</p>}
            
            {/* Content Rendering based on contentType */}
            <div className="prose prose-lg max-w-none mx-auto text-gray-800">
              {contentType === 'richtext' ? (
                <div dangerouslySetInnerHTML={{ __html: content }} />
              ) : (
                // Check if content contains HTML tags even when contentType is markdown
                /<[^>]*>/.test(content || '') ? (
                  <div dangerouslySetInnerHTML={{ __html: content || '' }} />
                ) : (
                  <ReactMarkdown>{content || ''}</ReactMarkdown> // Default to Markdown, handle empty content
                )
              )}
            </div>
            {/* Anchor for reactions placement */}
          </div>
        </article>

        {/* Reactions Bar */}
        <div className="mt-6 p-4 bg-white rounded shadow flex items-center gap-4">
          <span className="text-sm text-gray-600 mr-2">Reactions:</span>
          {[
            { key: 'like', label: '👍' },
            { key: 'love', label: '❤️' },
            { key: 'insightful', label: '💡' },
            { key: 'curious', label: '🤔' },
            { key: 'celebrate', label: '🎉' },
          ].map((r) => (
            <button
              key={r.key}
              onClick={() => handleReact(r.key)}
              disabled={reacting}
              className="px-3 py-1 rounded border hover:bg-gray-50 disabled:opacity-50"
              aria-label={`React ${r.key}`}
            >
              <span className="mr-2">{r.label}</span>
              <span className="text-sm text-gray-700">{reactions[r.key] || 0}</span>
            </button>
          ))}
        </div>

        {relatedArticles && relatedArticles.length > 0 && (
          <aside className="related-articles mt-12 pt-8 border-t border-gray-200">
            <h2 className="text-2xl font-semibold text-gray-800 mb-6">Related Articles</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {relatedArticles.map((relatedArticle) => (
                <Link to={`/article/${relatedArticle.slug}`} key={relatedArticle._id} className="block bg-white shadow-lg rounded-lg overflow-hidden hover:shadow-xl transition-shadow duration-300">
                  {relatedArticle.image && (
                    <img src={relatedArticle.image} alt={relatedArticle.title} className="w-full h-40 object-cover" />
                  )}
                  <div className="p-4">
                    <h3 className="text-lg font-semibold text-gray-900 mb-1 truncate" title={relatedArticle.title}>{relatedArticle.title}</h3>
                    {relatedArticle.category && <p className="text-xs text-blue-600 uppercase font-medium mb-2">{relatedArticle.category.name}</p>}
                    <p className="text-sm text-gray-600 line-clamp-3">{relatedArticle.description}</p>
                  </div>
                </Link>
              ))}
            </div>
          </aside>
        )}
      </div>
      <Footer />
    </>
  );
}

export default ArticleDetail;